// ============================================================
// FIRE RPG Market Chart — Hooks & API Layer
// Moved to src/hooks for consistent architecture across features.
// Wraps market chart API endpoints using axiosInstanceV2.
// ============================================================

import { notification } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstanceV2 from "@lhd/libs/axiosV2";

import type {
  MarketMaster,
  MarketStateSnapshot,
  MarketStateResponse,
  AssetConfig,
  IndexReference,
  MarketSettings,
  RoundResult,
  SaveRoundResultPayload,
  UpdateMarketSettingsPayload,
} from "@lhd/features/BusinessRpg/FireRpgMarketChart/types";
// --------------- Constants ---------------

const FIRE_RPG_BASE = "/fire_rpg";

export const MARKET_QUERY_KEYS = {
  MARKET_MASTER: "fire-rpg-market-master",
  MARKET_SETTINGS: "fire-rpg-market-settings",
  ROUND_RESULTS: "fire-rpg-round-results",
  MARKET_STATES: "fire-rpg-market-states",
} as const;

// --------------- Helpers: safe numeric parsing ---------------

/**
 * Parse a value that may arrive as string (decimal serialized by Rails)
 * or number. Returns 0 when the value is null / undefined.
 */
const toNum = (v: string | number | null | undefined): number => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// --------------- Response Normalizers ---------------

/**
 * Normalize raw asset_configs values that may be strings.
 */
export const normalizeAssetConfig = (
  raw: Record<string, any>,
): AssetConfig => ({
  code: raw.code,
  display_name: raw.display_name,
  calculation_type: raw.calculation_type,
  base_index: toNum(raw.base_index),
  base_value: toNum(raw.base_value),
  currency: raw.currency,
  step_rate_pct: toNum(raw.step_rate_pct),
  step_amount: raw.step_amount != null ? toNum(raw.step_amount) : null,
  rounding_unit: toNum(raw.rounding_unit),
  sort_order: toNum(raw.sort_order),
});

/**
 * Normalize raw index_references values that may be strings.
 */
export const normalizeIndexReference = (
  raw: Record<string, any>,
): IndexReference => ({
  index_value: toNum(raw.index_value),
  toyota_price: toNum(raw.toyota_price),
  softbank_price: toNum(raw.softbank_price),
  apple_price: toNum(raw.apple_price),
  amazon_price: toNum(raw.amazon_price),
  exchange_rate: toNum(raw.exchange_rate),
});

/**
 * Normalize global market settings (Toyota, SoftBank, Apple, Amazon, USD/JPY).
 */
export const normalizeMarketSettings = (
  raw: Record<string, any>,
): MarketSettings => ({
  id: raw?.id,
  toyota: {
    base_value: toNum(raw?.toyota?.base_value),
    step_rate_pct: toNum(raw?.toyota?.step_rate_pct),
  },
  softbank: {
    base_value: toNum(raw?.softbank?.base_value),
    step_rate_pct: toNum(raw?.softbank?.step_rate_pct),
  },
  apple: {
    base_value: toNum(raw?.apple?.base_value),
    step_rate_pct: toNum(raw?.apple?.step_rate_pct),
  },
  amazon: {
    base_value: toNum(raw?.amazon?.base_value),
    step_rate_pct: toNum(raw?.amazon?.step_rate_pct),
  },
  usd_jpy: {
    base_value: toNum(raw?.usd_jpy?.base_value),
  },
  created_at: raw?.created_at,
  updated_at: raw?.updated_at,
});

/**
 * Normalize a saved round result.
 */
export const normalizeRoundResult = (
  raw: Record<string, any>,
): RoundResult => ({
  id: raw.id,
  market_state_id: raw.market_state_id,
  round_number: raw.round_number,
  stock_dice_value:
    raw.stock_dice_value != null ? Number(raw.stock_dice_value) : null,
  forex_dice_value:
    raw.forex_dice_value != null ? Number(raw.forex_dice_value) : null,
  annual_event_value: raw.annual_event_value ?? null,
  five_year_event_value: raw.five_year_event_value ?? null,
  created_at: raw.created_at,
  updated_at: raw.updated_at,
});

/**
 * Normalize full market master response.
 */
export const normalizeMarketMaster = (data: any): MarketMaster => ({
  asset_configs: (data?.asset_configs || []).map(normalizeAssetConfig),
  dice_rules: data?.dice_rules || { stock: [], forex: [] },
  settings: data?.settings ? normalizeMarketSettings(data.settings) : undefined,
  index_references: (data?.index_references || []).map(normalizeIndexReference),
  annual_events: data?.annual_events || [],
  five_year_events: data?.five_year_events || [],
  round_results: (data?.round_results || []).map(normalizeRoundResult),
});

/**
 * Normalize a single market state response —
 * prices / percentages / deltas may arrive as strings.
 */
export const normalizeMarketState = (
  raw: Record<string, any>,
): MarketStateResponse => ({
  id: raw.id,
  game_round_id: raw.game_round_id,
  round_number: raw.round_number,
  stock_dice_value:
    raw.stock_dice_value != null ? Number(raw.stock_dice_value) : null,
  forex_dice_value:
    raw.forex_dice_value != null ? Number(raw.forex_dice_value) : null,
  annual_event_card_id: raw.annual_event_card_id ?? null,
  five_year_event_card_id: raw.five_year_event_card_id ?? null,
  indices: {
    toyota: toNum(raw.indices?.toyota),
    softbank: toNum(raw.indices?.softbank),
    apple: toNum(raw.indices?.apple),
    amazon: toNum(raw.indices?.amazon),
    forex: toNum(raw.indices?.forex),
  },
  prices: {
    toyota: toNum(raw.prices?.toyota),
    softbank: toNum(raw.prices?.softbank),
    apple: toNum(raw.prices?.apple),
    amazon: toNum(raw.prices?.amazon),
    usd_jpy: toNum(raw.prices?.usd_jpy),
  },
  cumulative_percentages: {
    wage: toNum(raw.cumulative_percentages?.wage),
    price_level: toNum(raw.cumulative_percentages?.price_level),
    japan_rent: toNum(raw.cumulative_percentages?.japan_rent),
    us_rent: toNum(raw.cumulative_percentages?.us_rent),
    japan_interest: toNum(raw.cumulative_percentages?.japan_interest),
    us_interest: toNum(raw.cumulative_percentages?.us_interest),
    business: toNum(raw.cumulative_percentages?.business),
  },
  deltas: {
    toyota_index: toNum(raw.deltas?.toyota_index),
    softbank_index: toNum(raw.deltas?.softbank_index),
    apple_index: toNum(raw.deltas?.apple_index),
    amazon_index: toNum(raw.deltas?.amazon_index),
    forex_index: toNum(raw.deltas?.forex_index),
    wage: toNum(raw.deltas?.wage),
    price_level: toNum(raw.deltas?.price_level),
    japan_rent: toNum(raw.deltas?.japan_rent),
    us_rent: toNum(raw.deltas?.us_rent),
    japan_interest: toNum(raw.deltas?.japan_interest),
    us_interest: toNum(raw.deltas?.us_interest),
    business: toNum(raw.deltas?.business),
  },
  created_at: raw.created_at,
  updated_at: raw.updated_at,
});

// ============================================================
// 1. GET /api/v2/fire_rpg/market_master
//    Fetches global read-only master data (asset configs, dice
//    rules, index references, event cards). Cached indefinitely
//    because master data does not change during a game session.
// ============================================================

export const useMarketMaster = (enabled?: boolean) =>
  useQuery(
    [MARKET_QUERY_KEYS.MARKET_MASTER],
    async () => {
      const res = await axiosInstanceV2({
        method: "get",
        url: `${FIRE_RPG_BASE}/market_master`,
      });
      return normalizeMarketMaster(res.data);
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      enabled,
    },
  );

// ============================================================
// 2. GET /api/v2/fire_rpg/games/{gameId}/market_states
//    Fetches saved snapshots for all rounds of a game, filtered
//    by company. Used to restore state when re-entering a game.
// ============================================================

export const useMarketStates = (
  gameId: string | number | undefined,
  companyId: string | number | undefined,
) =>
  useQuery(
    [MARKET_QUERY_KEYS.MARKET_STATES, gameId, companyId],
    async () => {
      const res = await axiosInstanceV2({
        method: "get",
        url: `${FIRE_RPG_BASE}/games/${gameId}/market_states`,
        params: { company_id: companyId },
      });
      return ((res.data as any[]) || []).map(normalizeMarketState);
    },
    {
      enabled: Boolean(gameId && companyId),
    },
  );

// ============================================================
// 3. PUT /api/v2/fire_rpg/games/{gameId}/rounds/{roundNumber}/market_state
//    Saves the complete snapshot computed by FE for a round.
// ============================================================

export interface SaveMarketStateParams {
  gameId: string | number;
  roundNumber: number;
  payload: MarketStateSnapshot;
}

// In-flight request trackers for idempotency and request deduplication
const inFlightRoundSaves = new Map<number, Promise<any>>();
const inFlightMarketStateSaves = new Map<string, Promise<any>>();
const inFlightSettingsUpdate = { current: null as Promise<any> | null };

const generateIdempotencyKey = (prefix: string, identifier: string | number) =>
  `${prefix}-${identifier}-${Date.now()}`;

export const useSaveMarketState = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ gameId, roundNumber, payload }: SaveMarketStateParams) => {
      const dedupKey = `${gameId}-${roundNumber}`;
      const existing = inFlightMarketStateSaves.get(dedupKey);
      if (existing) {
        return existing;
      }

      const requestPromise = axiosInstanceV2({
        method: "put",
        url: `${FIRE_RPG_BASE}/games/${gameId}/rounds/${roundNumber}/market_state`,
        data: payload,
        headers: {
          "Idempotency-Key": generateIdempotencyKey("market-state", dedupKey),
        },
      })
        .then((res) => normalizeMarketState(res.data))
        .finally(() => {
          inFlightMarketStateSaves.delete(dedupKey);
        });

      inFlightMarketStateSaves.set(dedupKey, requestPromise);
      return requestPromise;
    },
    {
      onSuccess(_data, variables) {
        // Invalidate list cache so the latest snapshots are refetched
        queryClient.invalidateQueries([
          MARKET_QUERY_KEYS.MARKET_STATES,
          variables.gameId,
        ]);
        notification.success({
          message: "保存しました",
          description: `ラウンド ${variables.roundNumber} のマーケットデータを保存しました。`,
        });
      },
      onError() {
        notification.error({
          message: "保存に失敗しました",
          description:
            "マーケットデータの保存中にエラーが発生しました。もう一度お試しください。",
        });
      },
    },
  );
};

// ============================================================
// 4. GET /api/v2/fire_rpg/market_state/settings
// ============================================================

export const useMarketSettings = () =>
  useQuery([MARKET_QUERY_KEYS.MARKET_SETTINGS], async () => {
    return axiosInstanceV2({
      method: "get",
      url: `${FIRE_RPG_BASE}/market_state/settings`,
    }).then((res) => ({
      settings: normalizeMarketSettings(res.data?.settings),
      index_references: (res.data?.index_references || []).map(
        normalizeIndexReference,
      ),
    }));
  });

// ============================================================
// 5. PATCH /api/v2/fire_rpg/market_state/settings
//    Updates the 9 global settings values.
// ============================================================

export const useUpdateMarketSettings = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: UpdateMarketSettingsPayload) => {
      if (inFlightSettingsUpdate.current) {
        return inFlightSettingsUpdate.current;
      }

      const requestPromise = axiosInstanceV2({
        method: "patch",
        url: `${FIRE_RPG_BASE}/market_state/settings`,
        data: payload,
        headers: {
          "Idempotency-Key": generateIdempotencyKey("settings", "global"),
        },
      })
        .then((res) => ({
          settings: normalizeMarketSettings(res.data?.settings),
          asset_configs: (res.data?.asset_configs || []).map(
            normalizeAssetConfig,
          ),
          index_references: (res.data?.index_references || []).map(
            normalizeIndexReference,
          ),
        }))
        .finally(() => {
          inFlightSettingsUpdate.current = null;
        });

      inFlightSettingsUpdate.current = requestPromise;
      return requestPromise;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([MARKET_QUERY_KEYS.MARKET_SETTINGS]);
        queryClient.invalidateQueries([MARKET_QUERY_KEYS.MARKET_MASTER]);
      },
      onError: (err) => {
        console.error("Failed to update market settings", err);
      },
    },
  );
};

// ============================================================
// 6. GET /api/v2/fire_rpg/market_state/round_results
//    Fetches all saved round inputs.
// ============================================================

export const useRoundResults = () =>
  useQuery([MARKET_QUERY_KEYS.ROUND_RESULTS], async () => {
    return axiosInstanceV2({
      method: "get",
      url: `${FIRE_RPG_BASE}/market_state/round_results`,
    }).then((res) => ((res.data as any[]) || []).map(normalizeRoundResult));
  });

// ============================================================
// 7. PUT /api/v2/fire_rpg/market_state/round_results/{roundNumber}
//    Saves exactly 4 inputs of a round.
// ============================================================

export interface SaveRoundResultParams {
  roundNumber: number;
  payload: SaveRoundResultPayload;
}

export const useSaveRoundResult = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ roundNumber, payload }: SaveRoundResultParams) => {
      const existing = inFlightRoundSaves.get(roundNumber);
      if (existing) {
        return existing;
      }

      const requestPromise = axiosInstanceV2({
        method: "put",
        url: `${FIRE_RPG_BASE}/market_state/round_results/${roundNumber}`,
        data: payload,
        headers: {
          "Idempotency-Key": generateIdempotencyKey("round", roundNumber),
        },
      })
        .then((res) => normalizeRoundResult(res.data))
        .finally(() => {
          inFlightRoundSaves.delete(roundNumber);
        });

      inFlightRoundSaves.set(roundNumber, requestPromise);
      return requestPromise;
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([MARKET_QUERY_KEYS.ROUND_RESULTS]);
        queryClient.invalidateQueries([MARKET_QUERY_KEYS.MARKET_MASTER]);
      },
      onError: (err, variables) => {
        console.error(`Failed to save round ${variables.roundNumber}`, err);
      },
    },
  );
};
